import { BookOpenTextIcon, CalendarCheckIcon, CalendarXIcon, ClipboardCheckIcon, FolderSearchIcon, FrameIcon, LayersIcon, LayoutGridIcon, PaletteIcon, TimerIcon, UsersIcon, BrushIcon } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
// ЛИКОВНО СПРЕМНА™ – Serbian (ekavian, Cyrillic) copy for art teachers (ликовна култура, 1–8. разред). Source of truth: likovno-spremna/PRODUCTO.md.
export const BRAND = 'ЛИКОВНО СПРЕМНА™';
export const heroGuarantees = ['Тренутни дигитални приступ', 'Једнократна уплата', 'Без претплате', '7 дана гаранције задовољства'];
export const stats = [{
  value: '6',
  label: 'Дигиталних томова'
}, {
  value: '240',
  label: 'Страна у шест томова'
}, {
  value: '1–8',
  label: 'Разред – цела основна школа'
}, {
  value: '30',
  label: 'Уметничких дела за посматрање'
}, {
  value: '5',
  label: 'Бонуса у пакету'
}];
export const differenceStats = [{
  value: '1–8. разред',
  label: 'Учитељи у разредној настави и наставници ликовне културе у предметној'
}, {
  value: '6 дигиталних томова',
  label: 'Од годишњег плана и часова минут по минут до техника, историје уметности, пројеката и оцењивања'
}, {
  value: 'PDF спреман за штампу',
  label: 'Користите материјал на рачунару, таблету или телефону или га одштампајте'
}];
export const beforeItems: Array<{
  title: string;
  text: string;
}> = [{
  title: 'Час се смишља вече пре',
  text: 'Касно увече још тражите идеју за сутрашњи час ликовног – и опет се враћате на исте две технике.'
}, {
  title: 'Нема плана за целу годину',
  text: 'Знате да деци треба прогресија од септембра до јуна, али немате времена да направите годишњи план за сваки разред.'
}, {
  title: 'Различити нивои у истом одељењу',
  text: 'Неко једва држи четкицу, неко већ црта као мали уметник – а час мора да функционише за све.'
}, {
  title: 'Материјал се троши, а резултат изостаје',
  text: 'Без јасних корака рад се претвори у хаос од боја, а на крају часа нема довршених радова.'
}, {
  title: 'Изложбе и празници стижу изненада',
  text: 'Нова година, Свети Сава, Дан школе – и опет се пано прави у последњем тренутку.'
}, {
  title: 'Оцењивање одузима вечери',
  text: 'Без критеријума и рубрика тешко је објаснити оцену ученику, родитељу и директору.'
}];
export const afterItems = ['Годишњи план за сваки разред од 1. до 8.', 'Распоред тема по месецима, септембар–јун', '12 часова минут по минут у Тому 1', 'Материјал, мотивација, активност и вредновање уз сваки час', 'Радни листови у 3 нивоа', 'Технике цртања, сликања и вајања по разредима', '30 уметничких дела са листовима за разговор', 'Пројекти за празнике и изложбу на крају године', 'Критеријуми, рубрике, портфолио и 100 готових коментара'];
export const recognizeItems: Array<{
  icon: LucideIcon;
  title: string;
  text: string;
}> = [{
  icon: CalendarXIcon,
  title: 'Вечери одлазе на припрему',
  text: 'Да ли после наставе проводите вечери смишљајући часове ликовне културе за следећу недељу?'
}, {
  icon: FolderSearchIcon,
  title: 'Тражите идеје по интернету',
  text: 'Да ли стално тражите радове и технике који одговарају узрасту вашег одељења?'
}, {
  icon: UsersIcon,
  title: 'Мешовито одељење',
  text: 'Да ли вам требају задаци који функционишу и за ученике којима цртање иде теже и за оне најталентованије?'
}, {
  icon: PaletteIcon,
  title: 'Увек исте технике',
  text: 'Желите више од воштаних бојица и темпере – вајање, колаж, графику и рад са рециклираним материјалом?'
}, {
  icon: ClipboardCheckIcon,
  title: 'Оцењивање без јасних мерила',
  text: 'Желите готове критеријуме, рубрике и коментаре за ученике и родитеље?'
}, {
  icon: LayoutGridIcon,
  title: 'Недостаје јасна структура',
  text: 'Желите да све буде на једном месту и да за пет минута нађете прави час пре него што уђете у учионицу?'
}];

// Spine colours mirror the physical box (blue, yellow, orange, blue, yellow, orange).
export const volumes: Array<{
  number: string;
  level: string;
  title: string;
  tagline: string;
  intro: string;
  items: string[];
  outro: string;
  note?: string;
  spine: string;
  rule: string;
}> = [{
  number: 'Том 01',
  level: 'План',
  title: 'Годишњи план и часови минут по минут',
  tagline: 'Тачно знате шта радите на сваком часу – од септембра до јуна.',
  intro: 'Први том вам даје годишњи план за сваки разред и дванаест часова које можете одмах да одржите.',
  items: ['Годишњи план за 1–8. разред по програму наставе и учења', 'Фонд часова: 1. разред 36, 2–5. разред 72, 6–8. разред 36', 'Распоред тема по месецима', '12 часова минут по минут', 'Уз сваки час: материјал, мотивација, активност и вредновање', 'Радни листови у 3 нивоа', 'Контролна листа пред час'],
  outro: 'Уђите у час са планом – сваки пут.',
  spine: 'bg-blue-700 text-white',
  rule: 'border-white/30'
}, {
  number: 'Том 02',
  level: 'Технике',
  title: 'Цртање и сликање',
  tagline: 'Технике по разредима – корак по корак.',
  intro: 'Други том води кроз цртачке и сликарске технике прилагођене узрасту, од прве линије до круга боја.',
  items: ['Технике по разредима', 'Оловка, туш и фломастер', 'Темпера, акварел и пастел', 'Линија, облик и површина', 'Боја, контраст и круг боја', 'Упутства корак по корак', 'Честе грешке и савети'],
  outro: 'Свака техника објашњена тако да је ученици могу сами да примене.',
  spine: 'bg-yellow-400 text-slate-900',
  rule: 'border-slate-900/20'
}, {
  number: 'Том 03',
  level: 'Облик',
  title: 'Вајање, колаж и примењена уметност',
  tagline: 'Ликовно и изван папира.',
  intro: 'Трећи том доноси рад у простору и са различитим материјалима – од глине до дизајна.',
  items: ['Глина и пластелин', 'Колаж и рад са папиром', 'Рециклажа – уметност од отпадног материјала', 'Графика: отисак', 'Дизајн и примењена уметност', 'Списак материјала уз сваки рад'],
  outro: 'Нове технике које деца памте.',
  spine: 'bg-orange-600 text-white',
  rule: 'border-white/30'
}, {
  number: 'Том 04',
  level: 'Уметност',
  title: 'Историја уметности и посматрање дела',
  tagline: 'Деца уче да гледају, разговарају и разумеју.',
  intro: 'Четврти том садржи 30 уметничких дела из јавног домена са листовима за посматрање и разговор.',
  items: ['30 дела из јавног домена', 'Надежда Петровић, Паја Јовановић, Сава Шумановић', 'Ван Гог, Моне и други велики сликари', 'Средњовековно српско сликарство: Студеница, Милешева – „Бели анђео“', 'Листови „Посматрам и разговарам“', 'Питања за разговор по узрасту'],
  outro: 'Уметност која постаје блиска деци.',
  spine: 'bg-blue-700 text-white',
  rule: 'border-white/30'
}, {
  number: 'Том 05',
  level: 'Пројекти',
  title: 'Пројекти и школске изложбе',
  tagline: 'Празници и изложбе – без јурњаве у последњем тренутку.',
  intro: 'Пети том има пројекат за сваки месец и упутство за изложбу на крају године.',
  items: ['Пројекат за сваки месец', 'Нова година', 'Свети Сава', 'Дан школе', 'Ускрс', 'Изложба на крају године корак по корак'],
  outro: 'Пано и изложба спремни на време.',
  spine: 'bg-yellow-400 text-slate-900',
  rule: 'border-slate-900/20'
}, {
  number: 'Том 06',
  level: 'Оцењивање',
  title: 'Оцењивање и портфолио',
  tagline: 'Јасна мерила – за ученике, родитеље и директора.',
  intro: 'Шести том садржи критеријуме, рубрике и портфолио, уз готове коментаре за повратну информацију.',
  items: ['Критеријуми по разредима', 'Описно оцењивање (1–3. разред)', 'Бројчано оцењивање (4–8. разред)', 'Рубрике за ликовне радове', 'Портфолио ученика', '100 готових коментара'],
  outro: 'Мање објашњавања – више јасних оцена.',
  note: 'Независан приручник за наставнике. Није издање нити одобрење Министарства просвете.',
  spine: 'bg-orange-600 text-white',
  rule: 'border-white/30'
}];
export const volumesIntro = {
  title: '6 дигиталних томова за ликовну културу',
  text: 'Комплетан систем који вам помаже да испланирате и водите часове ликовне културе током целе школске године. Сваки том има око 40 страна са готовим часовима, техникама и радним листовима.'
};
export const packItems: Array<{
  icon: LucideIcon;
  title: string;
  text: string;
}> = [{
  icon: TimerIcon,
  title: 'Часови минут по минут',
  text: 'Крај празном папиру. Мотивација, активност и вредновање – са тачним минутима и материјалом.'
}, {
  icon: LayersIcon,
  title: 'Радни листови у 3 нивоа',
  text: 'Исти задатак у три верзије, да цело одељење ради заједно, свако на свом нивоу.'
}, {
  icon: BrushIcon,
  title: 'Технике по разредима',
  text: 'Цртање, сликање, вајање, колаж и графика – прилагођено узрасту од 1. до 8. разреда.'
}, {
  icon: CalendarCheckIcon,
  title: 'План целе године',
  text: 'Годишњи план за сваки разред, по програму наставе и учења, са темама по месецима.'
}, {
  icon: FrameIcon,
  title: 'Уметност која се посматра',
  text: '30 дела из јавног домена – од фресака из Милешеве до Надежде Петровић и Ван Гога.'
}, {
  icon: BookOpenTextIcon,
  title: 'Изложбе и оцењивање',
  text: 'Пројекти за празнике, изложба на крају године, рубрике, портфолио и готови коментари.'
}];
// The «Погледајте унутра» carousel reads data/samples.ts (hidden while empty).
export const pageTypes = ['Часови минут по минут', 'Радни листови у 3 нивоа', 'Годишњи план', 'Технике корак по корак'];
export const bonuses: Array<{
  title: string;
  text: string;
  items: string[];
  benefit: string;
}> = [{
  title: '50 ликовних загревања од 5 минута',
  text: 'Кратке ликовне игре за почетак часа, без посебне припреме, да се одељење одмах укључи.',
  items: ['50 загревања', 'По 5 минута', 'За 1–8. разред', 'Без посебног материјала'],
  benefit: 'Сваки час почиње брзо и са пажњом.'
}, {
  title: 'Пакет за замену: 12 часова без припреме',
  text: 'Када сте одсутни, колега који вас мења има јасан час са једноставним упутствима.',
  items: ['12 готових часова', 'Једноставна упутства', 'За колегу на замени', 'Спремно за штампу'],
  benefit: 'Ниједан изгубљен час када сте одсутни.'
}, {
  title: 'Сарадња са родитељима: обрасци и поруке',
  text: 'Готове поруке и обрасци за родитеље – од списка прибора до позива на изложбу.',
  items: ['Поруке за родитеље', 'Списак ликовног прибора', 'Позив на изложбу', 'Обрасци за штампу'],
  benefit: 'Јасна комуникација без писања од нуле.'
}, {
  title: 'Постери за кабинет: ликовни појмови',
  text: 'Постери са основним ликовним појмовима за зид учионице или ликовног кабинета.',
  items: ['Боја', 'Линија', 'Облик', 'Простор'],
  benefit: 'Деца виде кључне појмове на сваком часу.'
}, {
  title: 'Ликовни календар: теме и конкурси кроз годину',
  text: 'Теме и важни датуми по месецима, уз подсетник за ликовне конкурсе и школске изложбе.',
  items: ['Теме по месецима', 'Важни датуми', 'Подсетник за конкурсе', 'Подсетник за изложбе'],
  benefit: 'Цела година на једном месту.'
}];
export const steps: Array<{
  title: string;
  text: string;
}> = [{
  title: 'Отворите пакет',
  text: 'Преузмите ЛИКОВНО СПРЕМНА™ и отворите том који вам треба.'
}, {
  title: 'Изаберите разред и час',
  text: 'Пронађите час, технику или пројекат за своје одељење и месец.'
}, {
  title: 'Припремите се за 5 минута',
  text: 'Прочитајте ток часа, спремите материјал и одштампајте радни лист у потребном нивоу.'
}, {
  title: 'Уђите у час са планом',
  text: 'Одштампајте страну или је отворите на таблету – и почните час.'
}];
export const audienceItems = ['Учитељ сте и предајете ликовну културу од 1. до 4. разреда', 'Наставник сте ликовне културе од 5. до 8. разреда', 'Желите годишњи план за сваки разред', 'Радите са одељењима различитог нивоа', 'Желите више техника од цртања и темпере', 'Припремате школске изложбе и паное за празнике', 'Желите јасне критеријуме за оцењивање', 'Желите да вам вечери буду слободне'];
export const timeValueTasks = ['Тражење идеја и радова на интернету', 'Прављење годишњег плана', 'Писање тока часа и радних листова', 'Прилагођавање задатака за различите нивое', 'Припрема изложби и оцењивање радова'];
export const offerRows: Array<{
  label: string;
  detail: string;
}> = [{
  label: '6 дигиталних томова – 240 страна',
  detail: 'Годишњи план и часови, цртање и сликање, вајање и колаж, историја уметности, пројекти и изложбе, оцењивање'
}, {
  label: '5 бонуса без доплате',
  detail: '50 ликовних загревања, Пакет за замену, Сарадња са родитељима, Постери за кабинет и Ликовни календар'
}, {
  label: 'За 1–8. разред',
  detail: '12 часова минут по минут, радни листови у 3 нивоа и 30 уметничких дела'
}];
export const offerIncludes = ['Тренутни дигитални приступ', 'PDF фајлови које чувате', 'Спремно за штампу', 'Ништа се не шаље поштом'];
// Serbian dinar (RSD), Serbian notation: "2.490 дин." = Market «Србија».
export const PRICE = '2.490 дин.';
export const PRICE_BEFORE = '5.990 дин.';

// Only add authentic reviews of ЛИКОВНО СПРЕМНА. The section stays hidden while empty.
export const testimonials: Array<{
  quote: string;
  name: string;
  location: string;
}> = [];
export const faqs = [{
  q: 'Како добијам материјал?',
  a: 'ЛИКОВНО СПРЕМНА™ се испоручује дигитално. После куповине на мејл добијате приступ PDF фајловима које можете да преузмете и сачувате. Материјал користите на рачунару, таблету или телефону и штампате стране које су вам потребне.'
}, {
  q: 'За које разреде је материјал?',
  a: 'За ликовну културу од 1. до 8. разреда основне школе – и за учитеље у разредној настави и за наставнике ликовне културе у предметној настави.'
}, {
  q: 'Да ли је материјал усклађен са програмом?',
  a: 'Годишњи план је направљен по програму наставе и учења ликовне културе, са фондом часова за сваки разред (1. разред 36, 2–5. разред 72, 6–8. разред 36). ЛИКОВНО СПРЕМНА™ је независан приручник за наставнике и није издање нити одобрење Министарства просвете.'
}, {
  q: 'Колико траје један час?',
  a: 'Часови су планирани за 45 минута, минут по минут: мотивација, активност и вредновање. Блокове можете да скратите или продужите, или да спојите у двочас.'
}, {
  q: 'Шта ако у одељењу имам ученике различитог нивоа?',
  a: 'Радни листови постоје у 3 нивоа, па цело одељење ради исту тему – свако на свом нивоу.'
}, {
  q: 'Који материјал је потребан за часове?',
  a: 'Углавном оно што већ имате у школи: оловке, бојице, темпере, папир, лепак и маказе. Уз сваки час и рад пише тачан списак материјала, а многи радови користе рециклирани материјал.'
}, {
  q: 'Да ли су уметничка дела у Тому 4 слободна за употребу?',
  a: 'Да. У Тому 4 су дела из јавног домена – од средњовековних фресака до Надежде Петровић, Паје Јовановића, Саве Шумановића, Ван Гога и Монеа.'
}, {
  q: 'Има ли материјала за изложбе и празнике?',
  a: 'Да. Том 5 има пројекат за сваки месец, радове за Нову годину, Светог Саву, Дан школе и Ускрс и упутство за изложбу на крају године.'
}, {
  q: 'Да ли морам све да одштампам?',
  a: 'Не. Сами бирате које стране штампате. Многи наставници ток часа користе на таблету или телефону, а радне листове и постере одштампају.'
}, {
  q: 'Да ли је пет бонуса укључено у цену?',
  a: 'Да. Свих пет бонуса је у пакету без доплате.'
}, {
  q: 'Да ли је ово претплата?',
  a: 'Не. Плаћате једном и добијате приступ дигиталном материјалу. Без месечних плаћања.'
}, {
  q: 'Постоји ли гаранција?',
  a: 'Да. ЛИКОВНО СПРЕМНА™ има гаранцију задовољства од 7 дана. Ако нисте задовољни материјалом, можете да нам пишете и да добијете повраћај новца у гарантном року, према условима гаранције.'
}];
